/**
 * @fileoverview SummaryChatSheet — wires the generic ChatSheet to the
 * chat backend for a specific summarization task.
 *
 * Supports multiple conversation threads: users can start new chats
 * and switch between previous conversations.
 */

import { useState, useCallback, useEffect, useMemo } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { convexQuery, useConvexAction } from "@convex-dev/react-query";
import { convexQueryOrIdle } from "@/lib/convex-query-idle";
import { useThreadMessages } from "@convex-dev/agent/react";
import type { Message } from "@convex-dev/agent";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import {
  ChatSheet,
  type ChatMessageData,
  type ChatUserInfo,
} from "@/ui/composed/chat";
import { DocumentDetail } from "../documents/DocumentDetail";
import { UsageBanner } from "@/components/agents/universal/UsageBanner";
import { ProposalReviewStack } from "@/components/agents/universal/StructuredDiffCard";

/** Thread messages we render — user/assistant turns only (no tool/system). */
type ChatTurnMessage = Extract<Message, { role: "user" | "assistant" }>;

export interface ChatThread {
  threadId: string;
  agentName: string;
  createdAt: number;
  updatedAt: number;
}

interface SummaryChatSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  taskId: Id<"summarizationTasks">;
  jobId?: Id<"jobs">;
  agentName: string;
  /** Ignored — summary output is fetched server-side from the task */
  summaryOutput?: unknown;
}

export function SummaryChatSheet({
  open,
  onOpenChange,
  taskId: summarizationTaskId,
  jobId: jobIdProp,
  agentName,
}: SummaryChatSheetProps) {
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
  const [isNewThread, setIsNewThread] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [waitingForResponse, setWaitingForResponse] = useState(false);
  const [viewingDocumentId, setViewingDocumentId] =
    useState<Id<"documents"> | null>(null);

  // Fetch current user for avatar display
  const { data: currentUser } = useQuery(
    convexQuery(api.users.queries.getCurrentUser, {}),
  );
  const userInfo = useMemo<ChatUserInfo | undefined>(() => {
    if (!currentUser) return undefined;
    return {
      avatarUrl: currentUser.avatarUrl,
      firstName: currentUser.firstName,
      lastName: currentUser.lastName,
      email: currentUser.email,
    };
  }, [currentUser]);

  // Fetch task detail to resolve jobId when not passed as prop
  const { data: taskDetail } = useQuery(
    convexQuery(
      api.jobs.summarization.publicQueries.getSummarizationTaskDetail,
      { taskId: summarizationTaskId },
    ),
  );
  const jobId = jobIdProp ?? taskDetail?.jobId;

  // Need orgId for the AI-usage banner — pull it from the job. No query
  // is built while jobId is still resolving from the task detail (a
  // placeholder id would be sent to the server and rejected).
  const { data: job } = useQuery(
    convexQueryOrIdle(api.jobs.queries.getJob, jobId ? { jobId } : undefined),
  );
  const orgId = job?.orgId as Id<"organizations"> | undefined;

  // List all threads for this task
  const { data: threads } = useQuery(
    convexQuery(api.chat.publicQueries.listChatThreads, {
      summarizationTaskId,
    }),
  );

  // Auto-select most recent thread on first open (but not when user clicked "New")
  useEffect(() => {
    if (!activeThreadId && !isNewThread && threads && threads.length > 0) {
      setActiveThreadId(threads[0].threadId);
    }
  }, [activeThreadId, isNewThread, threads]);

  // Subscribe to active thread messages with real-time streaming
  const threadMessages = useThreadMessages(
    api.chat.publicQueries.listChatMessages,
    activeThreadId ? { threadId: activeThreadId } : "skip",
    { initialNumItems: 50, stream: true },
  );

  // Optimistic user message — show immediately before backend round-trip
  const [pendingMessage, setPendingMessage] = useState<string | null>(null);

  // Map agent thread messages to ChatMessageData format
  const rawMessages = threadMessages?.results ?? [];
  type RawThreadMessage = (typeof rawMessages)[number];

  const realMessages: ChatMessageData[] = rawMessages
    .filter((m): m is RawThreadMessage & { message: ChatTurnMessage } => {
      const message = m.message;
      if (message?.role !== "user" && message?.role !== "assistant")
        return false;
      const content = message.content;
      if (!content) return false;
      if (typeof content === "string") return content.trim().length > 0;
      if (Array.isArray(content)) {
        return content.some(
          (part) => part.type === "text" && part.text?.trim(),
        );
      }
      return false;
    })
    .map((m) => {
      const content = m.message.content;
      const text =
        typeof content === "string"
          ? content
          : content
              .flatMap((p) => (p.type === "text" ? [p.text] : []))
              .join("");

      return {
        id: m.key ?? m._id ?? String(m._creationTime),
        role: m.message.role,
        content: text,
        createdAt: m._creationTime,
        isStreaming: m.streaming === true,
      };
    });

  // Clear optimistic message once the real one appears in the stream
  useEffect(() => {
    if (
      pendingMessage &&
      realMessages.some(
        (m) => m.role === "user" && m.content === pendingMessage,
      )
    ) {
      setPendingMessage(null);
    }
  }, [realMessages, pendingMessage]);

  // Merge optimistic message with real messages
  const messages: ChatMessageData[] = useMemo(() => {
    if (!pendingMessage) return realMessages;
    // Already arrived from backend — just use real
    if (
      realMessages.some(
        (m) => m.role === "user" && m.content === pendingMessage,
      )
    ) {
      return realMessages;
    }
    return [
      ...realMessages,
      {
        id: "optimistic-pending",
        role: "user" as const,
        content: pendingMessage,
        createdAt: Date.now(),
      },
    ];
  }, [realMessages, pendingMessage]);

  // Derive thinking + tool activity state from raw thread messages.
  // This correctly covers the FULL tool-use cycle, not just the first response.
  const { isAgentThinking, activeToolCalls } = useMemo(() => {
    const raw = threadMessages?.results ?? [];

    // No messages yet — rely on manual flag
    if (raw.length === 0) {
      return {
        isAgentThinking: waitingForResponse,
        activeToolCalls: [] as string[],
      };
    }

    // Collect tool-call names after the last user message (always, before any early returns)
    const tools: string[] = [];
    for (let i = raw.length - 1; i >= 0; i--) {
      const m = raw[i];
      if (m.message?.role === "user") break;
      if (m.message?.role === "assistant" && Array.isArray(m.message.content)) {
        for (const part of m.message.content) {
          if (part.type === "tool-call" && part.toolName) {
            tools.push(part.toolName);
          }
        }
      }
    }

    const lastRaw = raw[raw.length - 1];
    const lastRole = lastRaw.message?.role;

    // If the last message is a streaming assistant → not "thinking".
    // Return empty tools so ChatMessageList can start its fade-out.
    if (lastRaw.streaming && lastRole === "assistant") {
      return { isAgentThinking: false, activeToolCalls: [] as string[] };
    }

    // Agent is still working if:
    // - waiting for first response
    // - last raw message is a tool result (agent processing it)
    // - last raw message is an assistant with tool-call parts (tool executing)
    const lastContent = lastRaw.message?.content;
    const hasToolCallParts =
      lastRole === "assistant" &&
      Array.isArray(lastContent) &&
      lastContent.some((p) => p.type === "tool-call");

    const isWorking =
      waitingForResponse || lastRole === "tool" || hasToolCallParts;

    // Only return tools while actively working — lets ChatMessageList handle fade
    return {
      isAgentThinking: isWorking,
      activeToolCalls: isWorking ? tools : [],
    };
  }, [threadMessages?.results, waitingForResponse]);

  // Clear waitingForResponse once the agent is no longer thinking
  // (prevents it from staying stale across conversations)
  useEffect(() => {
    if (waitingForResponse && !isAgentThinking) {
      setWaitingForResponse(false);
    }
  }, [waitingForResponse, isAgentThinking]);

  // Send message action
  const { mutateAsync: sendAction } = useMutation({
    mutationFn: useConvexAction(api.chat.actions.sendChatMessage),
  });

  const handleSend = useCallback(
    async (message: string) => {
      if (!jobId) {
        console.warn(
          "[SummaryChatSheet] jobId not yet resolved, ignoring send",
        );
        return;
      }
      // Show optimistic user message + thinking indicator immediately
      setPendingMessage(message);
      setWaitingForResponse(true);
      setIsSending(true);
      try {
        const result = await sendAction({
          summarizationTaskId,
          jobId,
          message,
          // Pass activeThreadId so the backend reuses it (or creates new)
          threadId: activeThreadId ?? undefined,
        });
        // Set active thread on first message of a new conversation
        if (!activeThreadId) {
          setActiveThreadId(result.threadId);
          setIsNewThread(false);
        }
      } catch (err) {
        console.error("[SummaryChatSheet] Failed to send message:", err);
        setPendingMessage(null);
        setWaitingForResponse(false);
      } finally {
        setTimeout(() => setIsSending(false), 500);
      }
    },
    [sendAction, summarizationTaskId, jobId, activeThreadId],
  );

  const handleNewThread = useCallback(() => {
    setActiveThreadId(null);
    setIsNewThread(true);
  }, []);

  const handleSelectThread = useCallback((threadId: string) => {
    setActiveThreadId(threadId);
    setIsNewThread(false);
  }, []);

  const handleDocumentClick = useCallback((documentId: string) => {
    setViewingDocumentId(documentId as Id<"documents">);
  }, []);

  // Pending proposals — the agent stages these via `proposeEntityPatch`.
  // Scoped to the whole summarisation task (not just the active thread)
  // so changes staged in an earlier chat for this agent summary are
  // still visible and actionable instead of being orphaned.
  const { data: pendingProposals } = useQuery(
    convexQuery(api.agents.universal.proposals.listPendingProposalsForTask, {
      summarizationTaskId,
    }),
  );

  // Composite banner: stack pending proposals on top of the usage notice.
  const inputBanner = useMemo(() => {
    const proposals = pendingProposals ?? [];
    const hasProposals = proposals.length > 0;
    if (!hasProposals && !orgId) return null;
    return (
      <div className="flex flex-col">
        {hasProposals && (
          <div className="max-h-[360px] overflow-y-auto border-t border-border/60 bg-amber-500/[0.03] pb-1">
            <ProposalReviewStack
              proposals={proposals}
              scope={{ kind: "task", summarizationTaskId }}
            />
          </div>
        )}
        {orgId && <UsageBanner orgId={orgId} />}
      </div>
    );
  }, [pendingProposals, orgId, summarizationTaskId]);

  return (
    <>
      <ChatSheet
        open={open}
        onOpenChange={onOpenChange}
        title={`Chat — ${agentName}`}
        description="Ask Morgan questions about the summary and its source documents."
        messages={messages}
        onSend={handleSend}
        userInfo={userInfo}
        isSending={isSending}
        isThinking={isAgentThinking}
        activeToolCalls={activeToolCalls}
        emptyMessage={`Ask questions about the ${agentName}.\nYour Morgan chat has access to the summary and the source documents.`}
        inputPlaceholder="Ask about this summary..."
        inputBanner={inputBanner}
        threads={threads ?? []}
        activeThreadId={activeThreadId}
        onNewThread={handleNewThread}
        onSelectThread={handleSelectThread}
        onDocumentClick={handleDocumentClick}
      />
      <DocumentDetail
        documentId={viewingDocumentId}
        isViewer
        open={viewingDocumentId !== null}
        onClose={() => setViewingDocumentId(null)}
      />
    </>
  );
}
